import React, { useContext, useMemo, useState } from "react";
import Select from "react-select";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table
} from "reactstrap";
import { DataContext } from "../context/DataContext";
import {
  createResource,
  deleteResource,
  fetchResources,
  updateResource
} from "../context/api";
import { API_BASE } from "../config/api";

const allowedExtensions = ["pdf", "doc", "docx", "xls", "xlsx", "csv", "png", "jpg", "jpeg", "webp", "gif"];
const fileAccept = allowedExtensions.map(ext => `.${ext}`).join(",");

function getFileExtension(fileName) {
  return String(fileName || "").split(".").pop().toLowerCase();
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (!size) return "Unknown size";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  if (!value) return "";
  const normalized = String(value).replace(" ", "T");
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function visibleToLabel(resource) {
  const assigned = resource.assignedUsers || [];
  if (!assigned.length) return "Owner only";
  return assigned.map(user => user.name || user.email).join(", ");
}

const blankForm = {
  id: null,
  title: "",
  description: "",
  assignedUserIds: [],
  file: null,
  existingFileName: "",
  fileUrl: "",
  fileKey: "",
  mimeType: "",
  fileSize: null,
  archived: false,
};

export default function Resources() {
  const { data, setData } = useContext(DataContext);
  const resources = data.resources || [];
  const userProfile = data.userProfile || {};
  const users = data.users || [];
  const isAdmin = userProfile.is_superuser === true || userProfile.role === "admin";

  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(blankForm);
  const [selectedResource, setSelectedResource] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const refreshResources = async (includeArchived = showArchived) => {
    const sub = userProfile.sub;
    if (!sub) return;
    const result = await fetchResources(sub, { archived: includeArchived });
    setData(prev => ({ ...prev, resources: result.resources || [] }));
  };

  const userOptions = useMemo(() => {
    return users
      .filter(user => !user.is_archived)
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" }))
      .map(user => ({
        value: user.id,
        label: `${user.name || user.email}${user.id === userProfile.id ? " (you)" : ""}`,
      }));
  }, [users, userProfile.id]);

  const filteredResources = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return resources
      .filter(resource => showArchived || !resource.archived)
      .filter(resource => {
        if (!query) return true;
        return [
          resource.title,
          resource.description,
          resource.originalFilename,
          resource.owner?.name,
          resource.owner?.email,
          visibleToLabel(resource)
        ].some(value => String(value || "").toLowerCase().includes(query));
      });
  }, [resources, searchQuery, showArchived]);

  const canEditResource = (resource) =>
    resource?.canEdit === true || isAdmin || resource?.ownerUserId === userProfile.id;

  const closeModal = () => {
    setModalOpen(false);
    setSelectedResource(null);
    setFormData(blankForm);
    setError("");
  };

  const openAddModal = () => {
    setSelectedResource(null);
    setFormData(blankForm);
    setError("");
    setModalOpen(true);
  };

  const openResourceModal = (resource) => {
    setSelectedResource(resource);
    setFormData({
      id: resource.id,
      title: resource.title || "",
      description: resource.description || "",
      assignedUserIds: (resource.assignedUsers || []).map(user => user.id),
      file: null,
      existingFileName: resource.originalFilename || "",
      fileUrl: resource.fileUrl || "",
      fileKey: resource.fileKey || "",
      mimeType: resource.mimeType || "",
      fileSize: resource.fileSize || null,
      archived: resource.archived === true,
    });
    setError("");
    setModalOpen(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = getFileExtension(file.name);
    if (!allowedExtensions.includes(ext)) {
      setError("Unsupported file type. Use PDF, Word, Excel, CSV, PNG, JPG, WEBP, or GIF.");
      event.target.value = "";
      return;
    }

    setError("");
    setFormData(prev => ({ ...prev, file }));
  };

  const uploadResourceFile = async (file) => {
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const response = await fetch(`${API_BASE}/upload-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fileType: file.type,
        folder: "resources",
        userId: uploadId,
        originalFilename: file.name,
      }),
    });

    if (!response.ok) {
      let message = "Failed to get upload URL";
      try {
        const body = await response.json();
        message = body.message || message;
      } catch {
        message = await response.text();
      }
      throw new Error(message);
    }

    const { uploadUrl, key } = await response.json();
    const uploadResponse = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed (${uploadResponse.status})`);
    }

    const bucket = process.env.REACT_APP_S3_BUCKET;
    const region = process.env.REACT_APP_AWS_REGION;
    if (!bucket || !region) {
      throw new Error("S3 configuration missing");
    }

    return {
      fileUrl: `https://${bucket}.s3.${region}.amazonaws.com/${key}`,
      fileKey: key,
      originalFilename: file.name,
      mimeType: file.type,
      fileSize: file.size,
    };
  };

  const handleSave = async () => {
    const title = formData.title.trim();
    if (!title) {
      setError("Title is required.");
      return;
    }
    if (!formData.id && !formData.file) {
      setError("Choose a file to upload.");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      let fileFields = {};
      if (formData.file) {
        fileFields = await uploadResourceFile(formData.file);
      }

      const payload = {
        title,
        description: formData.description.trim() || null,
        assignedUserIds: formData.assignedUserIds || [],
        ...fileFields,
      };

      const saved = formData.id
        ? await updateResource(formData.id, payload)
        : await createResource(payload);

      setData(prev => {
        const current = prev.resources || [];
        const exists = current.some(resource => resource.id === saved.id);
        return {
          ...prev,
          resources: exists
            ? current.map(resource => resource.id === saved.id ? saved : resource)
            : [saved, ...current],
        };
      });
      closeModal();
    } catch (err) {
      console.error("Failed to save resource:", err);
      setError(err.message || "Failed to save resource.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveToggle = async (resource, archived) => {
    if (!canEditResource(resource)) return;
    setIsSaving(true);
    try {
      const updated = archived
        ? await deleteResource(resource.id)
        : await updateResource(resource.id, { archived: false });
      setData(prev => ({
        ...prev,
        resources: (prev.resources || []).map(item => item.id === updated.id ? updated : item),
      }));
      if (!showArchived && archived) {
        setData(prev => ({
          ...prev,
          resources: (prev.resources || []).filter(item => item.id !== updated.id),
        }));
      }
    } catch (err) {
      console.error("Failed to update resource archive state:", err);
      alert(err.message || "Failed to update resource.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleShowArchivedChange = async (event) => {
    const checked = event.target.checked;
    setShowArchived(checked);
    try {
      await refreshResources(checked);
    } catch (err) {
      console.error("Failed to refresh resources:", err);
    }
  };

  const editable = !selectedResource || canEditResource(selectedResource);
  const ownerId = selectedResource?.ownerUserId || userProfile.id;
  const shareOptions = userOptions.filter(option => option.value !== ownerId);
  const selectedShareOptions = shareOptions.filter(option =>
    (formData.assignedUserIds || []).includes(option.value)
  );

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <div>
                <CardTitle tag="h4">Resources</CardTitle>
                <p className="card-category mb-0">Documents shared with you and documents you manage</p>
              </div>
              <Button color="primary" onClick={openAddModal}>
                Upload Resource
              </Button>
            </CardHeader>
            <CardBody>
              <Row className="align-items-center mb-3">
                <Col md="8">
                  <Input
                    placeholder="Search resources..."
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.target.value)}
                  />
                </Col>
                <Col md="4" className="text-md-right mt-2 mt-md-0">
                  <Label check className="mb-0">
                    <Input
                      type="checkbox"
                      checked={showArchived}
                      onChange={handleShowArchivedChange}
                    />{" "}
                    Show archived
                  </Label>
                </Col>
              </Row>

              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Owner</th>
                    <th>Visible To</th>
                    <th>Updated</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResources.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        No resources found.
                      </td>
                    </tr>
                  )}
                  {filteredResources.map(resource => (
                    <tr key={resource.id} className={resource.archived ? "text-muted" : ""}>
                      <td>
                        <strong>{resource.title}</strong>
                        {resource.archived && <span className="badge badge-secondary ml-2">Archived</span>}
                        <div className="text-muted small">
                          {resource.originalFilename} · {formatFileSize(resource.fileSize)}
                        </div>
                        {resource.description && (
                          <div className="small">{resource.description}</div>
                        )}
                      </td>
                      <td>{resource.owner?.name || "Unknown"}</td>
                      <td>{visibleToLabel(resource)}</td>
                      <td>{formatDate(resource.updatedAt || resource.createdAt)}</td>
                      <td className="text-right">
                        <Button
                          color="link"
                          size="sm"
                          href={resource.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </Button>
                        <Button
                          color="link"
                          size="sm"
                          onClick={() => openResourceModal(resource)}
                        >
                          {canEditResource(resource) ? "Edit" : "View"}
                        </Button>
                        {canEditResource(resource) && (
                          <Button
                            color="link"
                            size="sm"
                            className={resource.archived ? "text-success" : "text-danger"}
                            disabled={isSaving}
                            onClick={() => handleArchiveToggle(resource, !resource.archived)}
                          >
                            {resource.archived ? "Restore" : "Delete"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={modalOpen} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          {formData.id ? (editable ? "Edit Resource" : "View Resource") : "Upload Resource"}
        </ModalHeader>
        <ModalBody>
          {error && <div className="alert alert-danger">{error}</div>}
          <Row>
            <Col md="7">
              <FormGroup>
                <Label for="resourceTitle">Title</Label>
                <Input
                  id="resourceTitle"
                  value={formData.title}
                  disabled={!editable}
                  onChange={event => setFormData(prev => ({ ...prev, title: event.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label for="resourceDescription">Description</Label>
                <Input
                  id="resourceDescription"
                  type="textarea"
                  rows="4"
                  value={formData.description}
                  disabled={!editable}
                  onChange={event => setFormData(prev => ({ ...prev, description: event.target.value }))}
                />
              </FormGroup>
              {editable && (
                <FormGroup>
                  <Label for="resourceFile">{formData.id ? "Replace File" : "File"}</Label>
                  <Input
                    id="resourceFile"
                    type="file"
                    accept={fileAccept}
                    onChange={handleFileChange}
                  />
                  <small className="text-muted">
                    Allowed: PDF, Word, Excel, CSV, PNG, JPG, WEBP, GIF.
                  </small>
                </FormGroup>
              )}
            </Col>
            <Col md="5">
              <FormGroup>
                <Label>Shared With</Label>
                <Select
                  isMulti
                  isDisabled={!editable}
                  options={shareOptions}
                  value={selectedShareOptions}
                  onChange={selected => {
                    setFormData(prev => ({
                      ...prev,
                      assignedUserIds: (selected || []).map(option => option.value),
                    }));
                  }}
                />
                <small className="text-muted">
                  Leave blank to keep this visible only to the owner.
                </small>
              </FormGroup>

              {formData.existingFileName && (
                <div className="mt-3">
                  <Label>Current File</Label>
                  <div>
                    <a href={formData.fileUrl} target="_blank" rel="noopener noreferrer">
                      {formData.existingFileName}
                    </a>
                  </div>
                  <small className="text-muted">{formatFileSize(formData.fileSize)}</small>
                </div>
              )}
              {formData.file && (
                <div className="mt-3">
                  <Label>Selected File</Label>
                  <div>{formData.file.name}</div>
                  <small className="text-muted">{formatFileSize(formData.file.size)}</small>
                </div>
              )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          {editable && (
            <Button color="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          )}
          <Button color="secondary" onClick={closeModal} disabled={isSaving}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
